"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.GlCartesian2dAxisGraphicsComponent = void 0;
const tslib_1 = require("tslib");
const cartesian_2d_axis_label_generator_1 = require("./cartesian-2d-axis-label-generator");
const rc_js_util_1 = require("rc-js-util");
const gl_cartesian_2d_trace_binder_1 = require("../traces/gl-cartesian-2d-trace-binder");
const core_1 = require("@visualization-tools/core");
/**
 * @public
 * Draws labels for cartesian 2d plots.
 */
class GlCartesian2dAxisGraphicsComponent {
    constructor(attachPoint, traceBinder, cameraBinder, axisLabelGenerator = new cartesian_2d_axis_label_generator_1.Cartesian2dAxisLabelGenerator(attachPoint)) {
        this.traceBinder = traceBinder;
        this.cameraBinder = cameraBinder;
        this.axisLabelGenerator = axisLabelGenerator;
        this.type = core_1.EGraphicsComponentType.Entity;
        this.config = new rc_js_util_1.Mat2.f32();
        this.bindings = {
            textureMappingAttribute: new core_1.GlFloatAttribute("axisGc_texMapping", new core_1.GlFloatBuffer(null), 4),
            spriteGeometryAttribute: new core_1.GlFloatAttribute("axisGc_spriteGeometry", new core_1.GlFloatBuffer(new Float32Array([
                0, 0,
                1, 0,
                0, 1,
                1, 1,
            ])), 2),
            configUniform: new core_1.GlMat2Uniform("axisGc_config", this.config),
            canvasToClipSizeUniform: new core_1.GlMat3Uniform("traceGc_screenToClipSize", new rc_js_util_1.Mat3.f32()),
            labelSpriteTexture2d: new core_1.GlTexture2d("axisGc_texture", null),
        };
        DEBUG_MODE && (0, core_1.assertBinder)(traceBinder, gl_cartesian_2d_trace_binder_1.TraceBinderIdentifier);
        this.transform = core_1.GlTransformProvider.createOne(this, this.traceBinder, (updateArg) => updateArg.userTransform, (entity) => entity);
        this.specification = core_1.GlProgramSpecification.mergeProgramSpecifications([
            traceBinder.specification,
            cameraBinder.specification,
            new core_1.GlProgramSpecification(core_1.GlShader.combineShaders([
                core_1.mat3MultiplyVec2Shader,
                vertexShader,
            ]), fragmentShader)
        ]);
    }
    getCacheId() {
        return [
            "GlAxisGraphicsComponent",
            this.cameraBinder.getBinderId(),
            this.traceBinder.getBinderId(),
        ].join("_");
    }
    getLinkableBinders() {
        return [this.traceBinder];
    }
    initialize(componentRenderer) {
        this.cameraBinder.initialize(componentRenderer);
        this.traceBinder.initialize(componentRenderer);
        this.bindings.canvasToClipSizeUniform.initialize(componentRenderer);
        this.bindings.configUniform.initialize(componentRenderer);
        this.bindings.textureMappingAttribute.initialize(componentRenderer);
        this.bindings.spriteGeometryAttribute.initialize(componentRenderer);
        this.bindings.labelSpriteTexture2d.initialize(componentRenderer);
    }
    onBeforeUpdate(componentRenderer, updateArg) {
        this.bindings.spriteGeometryAttribute.bindArray(componentRenderer);
        const frameId = componentRenderer.sharedState.frameCounter;
        this.bindings.canvasToClipSizeUniform.setData(updateArg.canvasDimensions.pixelToClipSize, frameId);
        this.bindings.canvasToClipSizeUniform.bind(componentRenderer);
    }
    update(entity, componentRenderer, updateArg) {
        this.cameraBinder.update(this.cameraBinder.getBinderData(entity, updateArg, componentRenderer), componentRenderer);
        this.traceBinder.updateData(entity);
        this.traceBinder.bindInstanced(componentRenderer, 1);
        const spriteLookup = this.axisLabelGenerator.update(entity, updateArg.canvasDimensions.dpr);
        this.bindings.textureMappingAttribute.setData(core_1.SpriteLookup.toTypedArray(spriteLookup), entity.changeId);
        this.bindings.textureMappingAttribute.bindArrayInstanced(componentRenderer, 1);
        const canvas = this.axisLabelGenerator.getCanvas();
        this.updateConfig(entity, canvas.width, canvas.height, componentRenderer.sharedState.frameCounter);
        this.bindings.configUniform.bind(componentRenderer);
        this.bindings.labelSpriteTexture2d.updateData(canvas);
        this.bindings.labelSpriteTexture2d.bind(componentRenderer);
        componentRenderer.drawInstancedArrays(componentRenderer.context.TRIANGLE_STRIP, 0, 4, entity.data.getTraceCount());
    }
    updateConfig(entity, textureWidth, textureHeight, frameId) {
        this.config[0] = entity.graphicsSettings.axisOptions.padding;
        this.config[1] = entity.graphicsSettings.axisOptions.padding;
        this.config[2] = 1 / textureWidth;
        this.config[3] = 1 / textureHeight;
        this.bindings.configUniform.setData(this.config, frameId);
    }
}
(0, tslib_1.__decorate)([
    rc_js_util_1.Once
], GlCartesian2dAxisGraphicsComponent.prototype, "getCacheId", null);
exports.GlCartesian2dAxisGraphicsComponent = GlCartesian2dAxisGraphicsComponent;
// @formatter:off
// language=GLSL prefix="#if __VERSION__ >=300 && __VERSION__ < 400 \n #define ATTRIBUTE in \n #define VARYING out \n #else \n #define ATTRIBUTE attribute \n #define VARYING varying \n #endif"
const vertexShader = new core_1.GlShader(`

ATTRIBUTE mediump vec4 axisGc_texMapping;
ATTRIBUTE lowp vec2 axisGc_spriteGeometry;
uniform mediump mat2 axisGc_config;
uniform highp mat3 traceGc_screenToClipSize;
VARYING mediump vec2 axisGc_texCoord;

vec2 axisGc_getTexDims()
{
    return axisGc_texMapping.zw;
}

vec2 axisGc_getTexOffset()
{
    return axisGc_texMapping.xy;
}

vec2 axisGc_getPadding()
{
    return axisGc_config[0];
}

vec2 axisGc_getTexScaleFactor()
{
    return axisGc_config[1];
}

vec2 axisGc_getTexCoord()
{
    float invertedY = mix(1., 0., axisGc_spriteGeometry.y);
    vec2 texPos = axisGc_getTexOffset() + axisGc_getTexDims() * vec2(axisGc_spriteGeometry.x, invertedY);
    return texPos * axisGc_getTexScaleFactor();
}

vec4 getPosition()
{
    vec2 near = traceConnector_getPosition()[0];
    vec2 far = traceConnector_getPosition()[1];
    vec3 nearTrace = v_2dCamera_getClipspaceCoords2d(near);

    vec2 offsetDir = sign(near - far);
    vec2 adjustedDimensions = mat3MultiplyVec2(traceGc_screenToClipSize, axisGc_getTexDims() * 0.5);
    adjustedDimensions = mix(-adjustedDimensions, adjustedDimensions, axisGc_spriteGeometry) + offsetDir * adjustedDimensions;
    vec2 labelPadding = mat3MultiplyVec2(traceGc_screenToClipSize, axisGc_getPadding()) * offsetDir.xy;

    return vec4(nearTrace.xy + adjustedDimensions + labelPadding, nearTrace.z, 1);
}

void main()
{
    vec2 texCoord = axisGc_getTexCoord();
    axisGc_texCoord = texCoord;
    gl_Position = getPosition();
}`);
// language=GLSL prefix="#if __VERSION__ >=300 && __VERSION__ < 400 \n #define VARYING in \n #define TEXTURE2D texture \n #else \n #define VARYING varying \n #define TEXTURE2D texture2D \n #endif \n void setFragmentColor(in lowp vec4 color);"
const fragmentShader = new core_1.GlShader(`
uniform highp sampler2D axisGc_texture;
VARYING mediump vec2 axisGc_texCoord;

void main()
{
    setFragmentColor(TEXTURE2D(axisGc_texture, axisGc_texCoord));
}`);
// @formatter:on
//# sourceMappingURL=gl-cartesian-2d-axis-graphics-component.js.map